import { Container, Sprite, randomRange } from '../../../odie';
import { WallBlockProps } from '../../defs/block';
import { blockPositionView } from '../../util/blockTools';
import { config } from '../../defs/config';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { MovieClip } from '@play-co/timestep-core/ui';
import { getMatch2AssetPath } from '../../util/assets';
import gsap from 'gsap';
import { waitFor } from '../../../lib/util/waitFor';
import { app } from '../../app';

const assets = {
  cloud: 'wall/block.wall.cloud.png',
  particle: 'wall/block.wall.particle.png',
  movieclip: {
    url: getMatch2AssetPath('blocks/wall/movieclip'),
    loadMethod: MovieClip.animationLoader,
  },
};

class WallLightning extends Container {
  private movieclip: MovieClip;
  private counter: LangBitmapFontTextView;

  constructor() {
    super();

    this.movieclip = new MovieClip({
      superview: this.view,
      url: assets.movieclip.url,
    });

    this.counter = new LangBitmapFontTextView({
      width: 120,
      height: 120,
      align: 'center',
      verticalAlign: 'center',
      localeText: () => '99',
      font: 'Title',
      size: 28,
      centerOnOrigin: true,
      color: '#FFFFFF',
      x: -3,
      y: 0,
    });

    this.movieclip.addViewSubstitution('placeholder_number', this.counter);
  }

  public updateCounter(count: number) {
    this.counter.localeText = () => count.toString();
  }

  public playIdle() {
    this.movieclip.play('lightning_idle', null, true);
  }

  public async playHit() {
    await this.movieclip.playAsync('lightning_hit');
  }

  public async playStrike() {
    await this.movieclip.playAsync('lightning_strike');
  }
}

class WallClouds extends Container {
  private clouds: Sprite[] = [];

  constructor(props: WallBlockProps) {
    super();

    const { width, height } = props;
    const numClouds = width * height;
    const offsetX = (width * config.tile.size) / 2;
    const offsetY = (height * config.tile.size) / 2 - 4;

    for (let i = 0; i < numClouds; i++) {
      const col = i % width;
      const row = Math.floor(i / width);
      const x = col * config.tile.size - offsetX;
      const y = row * config.tile.size - offsetY;

      const cloud = Sprite.from(assets.cloud);
      cloud.x = x + config.tile.size * 0.5;
      cloud.y = y + config.tile.size * 0.5;
      cloud.anchorX = 0.5;
      cloud.anchorY = 0.5;
      cloud.width = 110;
      cloud.height = 110;
      this.clouds.push(cloud);
      this.addChild(cloud);
    }
  }

  public playIdle() {
    for (const cloud of this.clouds) {
      const duration = randomRange(0.8, 1.2);
      const scale = randomRange(1.1, 1.2);
      const rotation = randomRange(-0.2, 0.2);
      gsap
        .to(cloud, {
          scaleX: scale,
          scaleY: scale,
          rotation,
          duration,
          ease: 'sine.inOut',
        })
        .yoyo(true)
        .repeat(-1);
    }
  }

  public async playDismiss() {
    const promises = [];
    for (const cloud of this.clouds) {
      gsap.killTweensOf(cloud);
      promises.push(
        gsap.to(cloud, {
          x: cloud.x + randomRange(-100, 100),
          y: cloud.y + randomRange(-100, 100),
          scaleX: 0,
          scaleY: 0,
          duration: 0.5,
          ease: 'sine.out',
        }),
      );
    }
    await Promise.all(promises);
  }
}

/*
   Wall block view
*/
export class WallBlockView extends Container {
  private clouds: WallClouds;
  private lightning: WallLightning;

  constructor(private props: WallBlockProps) {
    super();

    this.clouds = new WallClouds(props);
    this.clouds.playIdle();
    this.addChild(this.clouds);

    this.lightning = new WallLightning();
    this.lightning.updateCounter(props.count);
    this.lightning.playIdle();
    this.addChild(this.lightning);

    blockPositionView(this, props);
  }

  public static assets() {
    return Object.values(assets);
  }

  public updateCounter(count: number) {
    this.lightning.updateCounter(count);
  }

  public async playHit() {
    app.sound.play('puzzle-hat-destroy', { dupes: 5 });
    this.playParticles();
    await this.lightning.playHit();
    this.lightning.playIdle();
  }

  public async playStrike() {
    this.lightning.playStrike();
    await waitFor(1000);
    app.sound.play('puzzle-rocket-activate');
    await this.clouds.playDismiss();
  }

  private playParticles() {
    const PIDOUBLE = Math.PI * 2;
    const PIHALF = Math.PI * 0.5;
    const angle = Math.random() * PIDOUBLE;
    const numParticles = 8;
    for (let i = 0; i < numParticles; i++) {
      const particle = Sprite.from(assets.particle);
      const rotation = (PIDOUBLE / numParticles) * i + angle;
      particle.anchorX = particle.anchorY = 0.5;
      particle.scaleX = particle.scaleY = 2;
      particle.width = 54;
      particle.height = 34;
      particle.rotation = rotation - PIHALF;
      this.clouds.addChild(particle);
      gsap.to(particle, {
        x: Math.sin(rotation) * 100,
        y: Math.cos(rotation) * 100,
        scaleX: 0,
        scaleY: 0,
        ease: 'expo.out',
        duration: 0.5,
        onComplete: () => {
          particle.removeFromParent();
        },
      });
    }
  }
}
